import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Loader2, Check } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { sendMaatwerkOpleidingNotification } from '@/lib/webhook';

interface MaatwerkOpleidingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type ButtonState = 'idle' | 'loading' | 'success' | 'error';

const MaatwerkOpleidingDialog: React.FC<MaatwerkOpleidingDialogProps> = ({ open, onOpenChange }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [buttonState, setButtonState] = useState<ButtonState>('idle');
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setButtonState('loading');

    const formData = new FormData(e.currentTarget as HTMLFormElement);
    const data = {
      voornaam: formData.get('voornaam') as string,
      achternaam: formData.get('achternaam') as string,
      email: formData.get('email') as string,
      telefoon: formData.get('telefoon') as string || undefined,
      organisatie: formData.get('organisatie') as string,
      aantalDeelnemers: formData.get('aantalDeelnemers') as string || undefined,
      gewensteDuur: formData.get('gewensteDuur') as string || undefined,
      bericht: formData.get('bericht') as string || undefined,
    };

    try {
      const result = await sendMaatwerkOpleidingNotification(data);

      if (result.success) {
        setButtonState('success');
        toast({
          title: "Aanmelding verzonden!",
          description: "Bedankt voor je interesse. Je ontvangt binnenkort een bevestigingsmail en ik neem binnen 1 werkdag contact met je op.",
        });

        // Reset form
        (e.target as HTMLFormElement).reset();

        // Close dialog after 2 seconds
        setTimeout(() => {
          setButtonState('idle');
          onOpenChange(false);
        }, 2000);
      } else {
        setButtonState('error');
        toast({
          title: "Er ging iets mis",
          description: result.error || "Probeer het opnieuw of neem direct contact op via telefoon.",
          variant: "destructive",
        });

        setTimeout(() => {
          setButtonState('idle');
        }, 3000);
      }
    } catch (error) {
      console.error('Unexpected error:', error);
      setButtonState('error');
      toast({
        title: "Er ging iets mis",
        description: "Probeer het opnieuw of neem direct contact op via telefoon.",
        variant: "destructive",
      });

      setTimeout(() => {
        setButtonState('idle');
      }, 3000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-noto-serif">Aanmelden voor Maatwerk Opleiding</DialogTitle>
          <DialogDescription>
            Vul het formulier in en ik neem binnen 1 werkdag contact met je op om de mogelijkheden te bespreken.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="voornaam">Voornaam *</Label>
              <Input
                id="voornaam"
                name="voornaam"
                required
                disabled={isSubmitting}
                placeholder="Je voornaam"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="achternaam">Achternaam *</Label>
              <Input
                id="achternaam"
                name="achternaam"
                required
                disabled={isSubmitting}
                placeholder="Je achternaam"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email *</Label>
            <Input
              id="email"
              name="email"
              type="email"
              required
              disabled={isSubmitting}
              placeholder="je@email.nl"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="telefoon">Telefoon</Label>
            <Input
              id="telefoon"
              name="telefoon"
              type="tel"
              disabled={isSubmitting}
              placeholder="0612345678"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="organisatie">Organisatie *</Label>
            <Input
              id="organisatie"
              name="organisatie"
              required
              disabled={isSubmitting}
              placeholder="Je organisatie"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="aantalDeelnemers">Aantal deelnemers</Label>
              <Input
                id="aantalDeelnemers"
                name="aantalDeelnemers"
                type="number"
                min="1"
                disabled={isSubmitting}
                placeholder="Bijv. 8"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="gewensteDuur">Gewenste duur</Label>
              <Input
                id="gewensteDuur"
                name="gewensteDuur"
                disabled={isSubmitting}
                placeholder="Bijv. 3 dagen"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="bericht">Vragen of opmerkingen</Label>
            <Textarea
              id="bericht"
              name="bericht"
              rows={4}
              disabled={isSubmitting}
              placeholder="Beschrijf je specifieke behoeften, context of vragen (optioneel)"
            />
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Annuleren
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || buttonState === 'success'}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              {buttonState === 'loading' && (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Verzenden...
                </>
              )}
              {buttonState === 'success' && (
                <>
                  <Check className="mr-2 h-4 w-4" />
                  Verzonden!
                </>
              )}
              {buttonState === 'error' && 'Opnieuw proberen'}
              {buttonState === 'idle' && 'Aanmelden'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default MaatwerkOpleidingDialog;

